import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, RefreshCw, FileCheck2 } from "lucide-react";

const STATUS = {
  approved: ["Disetujui", "bg-emerald-100 text-emerald-700"],
  pending: ["Ditinjau WhatsApp", "bg-amber-100 text-amber-800"],
  received: ["Ditinjau WhatsApp", "bg-amber-100 text-amber-800"],
  rejected: ["Ditolak", "bg-red-100 text-red-700"],
  draft: ["Dibuat, belum diajukan", "bg-secondary text-muted-foreground"],
};

export function TwilioTemplates() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [errors, setErrors] = useState([]);

  const load = (refresh = false) => api.get(`/settings/twilio-templates${refresh ? "?refresh=true" : ""}`).then((r) => setRows(r.data.templates));
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async () => {
    setBusy("submit"); setErrors([]);
    try {
      const { data } = await api.post("/settings/twilio-templates/submit");
      setRows(data.templates); setErrors(data.errors || []);
      if (data.errors?.length) toast.warning("Sebagian template gagal diajukan"); else toast.success("Template diajukan ke WhatsApp — biasanya disetujui dalam beberapa menit s/d 24 jam");
    } catch (e) { const m = formatApiErrorDetail(e.response?.data?.detail); setErrors([m]); toast.error(m); }
    finally { setBusy(""); }
  };
  const refresh = async () => {
    setBusy("refresh");
    try { await load(true); toast.success("Status diperbarui"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mt-6" data-testid="twilio-templates-section">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <FileCheck2 className="w-5 h-5 text-primary" />
        <h4 className="font-heading font-bold">Template WhatsApp (kategori Utility, Bahasa Indonesia)</h4>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="rounded-sm" disabled={!!busy} onClick={refresh} data-testid="tpl-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy === "refresh" ? "animate-spin" : ""}`} /> Cek Status
          </Button>
          <Button size="sm" className="rounded-sm" disabled={!!busy} onClick={submit} data-testid="tpl-submit-btn">
            <Send className="w-4 h-4 mr-1" /> {busy === "submit" ? "Mengajukan…" : "Ajukan ke WhatsApp"}
          </Button>
        </div>
      </div>
      {errors.length > 0 && (
        <div className="mb-3 p-3 rounded-sm border border-red-200 bg-red-50 text-xs text-red-700 space-y-1" data-testid="tpl-errors">
          {errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}
      <div className="space-y-2">
        {rows.map((t) => {
          const [label, cls] = STATUS[t.status] || ["Belum dibuat", "bg-secondary text-muted-foreground"];
          return (
            <div key={t.kind} className="border border-border rounded-sm p-3" data-testid={`tpl-${t.kind}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-sm">{t.label}</span>
                <code className="text-[11px] bg-secondary px-1 rounded">{t.name}</code>
                <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-sm ${cls}`} data-testid={`tpl-status-${t.kind}`}>{label}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{t.body}</p>
              {t.buttons.length > 0 && (
                <div className="flex gap-2 mt-2">{t.buttons.map((b) => <span key={b} className="text-xs px-3 py-1 border border-primary/40 text-primary rounded-full">{b}</span>)}</div>
              )}
              {t.reason && <p className="text-xs text-red-600 mt-1">Alasan ditolak: {t.reason}</p>}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Variabel: {"{{1}}"} nama · {"{{2}}"} nomor dokumen · {"{{3}}"} total · {"{{4}}"} link PDF (approval: {"{{4}}"} rincian, {"{{5}}"} kode). Template disetujui otomatis dipakai — kolom ContentSid manual di atas tidak perlu diisi.</p>
    </div>
  );
}
